/**
 * Grandezas do domínio como objetos de valor.
 *
 * O ponto não é elegância: é que `Volume` e `Dinheiro` são tipos diferentes e o
 * compilador recusa somar um no outro. Num ERP onde m³ e R$ convivem em toda
 * tela, isso remove uma classe inteira de bug silencioso.
 *
 * Todos são imutáveis. Toda operação devolve uma instância nova.
 */
import { Decimal, ESCALA, EXIBICAO, ErroDominio, fixar, formatarPtBr, paraDecimal, } from "./numero.js";
/** Volume em metros cúbicos. Pode ser negativo — saldo de pátio pode ficar negativo. */
export class Volume {
    d;
    constructor(d) {
        this.d = d;
    }
    static ZERO = new Volume(new Decimal(0));
    static deM3(v, campo = "volume") {
        return new Volume(paraDecimal(v, campo));
    }
    static soma(volumes) {
        return volumes.reduce((acc, v) => acc.mais(v), Volume.ZERO);
    }
    mais(o) {
        return new Volume(this.d.plus(o.d));
    }
    menos(o) {
        return new Volume(this.d.minus(o.d));
    }
    vezes(n) {
        return new Volume(this.d.times(paraDecimal(n, "fator")));
    }
    dividido(n) {
        const divisor = paraDecimal(n, "divisor");
        if (divisor.isZero())
            throw new ErroDominio("divisão de volume por zero");
        return new Volume(this.d.dividedBy(divisor));
    }
    negativo() {
        return new Volume(this.d.negated());
    }
    absoluto() {
        return new Volume(this.d.abs());
    }
    ehZero() {
        return this.d.isZero();
    }
    ehNegativo() {
        return this.d.isNegative() && !this.d.isZero();
    }
    ehPositivo() {
        return this.d.isPositive() && !this.d.isZero();
    }
    /** -1, 0 ou 1 */
    compara(o) {
        return this.d.comparedTo(o.d);
    }
    menorQue(o) {
        return this.d.lessThan(o.d);
    }
    maiorOuIgual(o) {
        return this.d.greaterThanOrEqualTo(o.d);
    }
    /** Texto com 6 casas, pronto para NUMERIC(14,6). */
    paraBanco() {
        return fixar(this.d, ESCALA.VOLUME);
    }
    /** "29,782" — apresentação. Nunca volta para uma conta (design §6.3). */
    paraExibicao() {
        return formatarPtBr(this.d, EXIBICAO.VOLUME);
    }
    /** Acesso ao decimal interno, para composições dentro do domínio. */
    interno() {
        return this.d;
    }
    toString() {
        return this.paraBanco();
    }
    toJSON() {
        return this.paraBanco();
    }
}
/** Valor monetário em BRL, 2 casas. */
export class Dinheiro {
    d;
    constructor(d) {
        this.d = d;
    }
    static ZERO = new Dinheiro(new Decimal(0));
    static deReais(v, campo = "valor") {
        return new Dinheiro(paraDecimal(v, campo));
    }
    static soma(valores) {
        return valores.reduce((acc, v) => acc.mais(v), Dinheiro.ZERO);
    }
    mais(o) {
        return new Dinheiro(this.d.plus(o.d));
    }
    menos(o) {
        return new Dinheiro(this.d.minus(o.d));
    }
    vezes(n) {
        return new Dinheiro(this.d.times(paraDecimal(n, "fator")));
    }
    ehZero() {
        return this.arredondado().isZero();
    }
    compara(o) {
        return this.arredondado().comparedTo(o.arredondado());
    }
    arredondado() {
        return this.d.toDecimalPlaces(ESCALA.DINHEIRO, Decimal.ROUND_HALF_UP);
    }
    /** Texto com 2 casas, pronto para NUMERIC(15,2). */
    paraBanco() {
        return fixar(this.d, ESCALA.DINHEIRO);
    }
    /** "38.535,12" — sem símbolo de moeda. */
    paraExibicao() {
        return formatarPtBr(this.d, EXIBICAO.DINHEIRO);
    }
    interno() {
        return this.d;
    }
    toString() {
        return this.paraBanco();
    }
    toJSON() {
        return this.paraBanco();
    }
}
/**
 * Preço unitário — por m³, por peça, por metro linear.
 *
 * Guarda 6 casas de propósito. A spec funcional §4.3 manda que a autoridade seja
 * o TOTAL, com o preço unitário derivado dele; para essa derivação fechar dentro
 * da tolerância da SEFAZ, o preço precisa de mais casas que o dinheiro.
 */
export class Preco {
    d;
    constructor(d) {
        this.d = d;
    }
    static ZERO = new Preco(new Decimal(0));
    static deReais(v, campo = "preço") {
        return new Preco(paraDecimal(v, campo));
    }
    /**
     * Preço unitário derivado do total (§4.3 — a autoridade é o total).
     * Devolve ZERO quando a quantidade é zero, em vez de estourar: linha zerada
     * é estado normal de digitação, não erro.
     */
    static derivadoDoTotal(total, quantidade) {
        if (quantidade.ehZero())
            return Preco.ZERO;
        return new Preco(total.interno().dividedBy(quantidade.interno()));
    }
    /** preço × volume → dinheiro, arredondado a 2 casas na saída. */
    vezesVolume(v) {
        return Dinheiro.deReais(this.d.times(v.interno()));
    }
    vezes(n) {
        return new Preco(this.d.times(paraDecimal(n, "fator")));
    }
    ehZero() {
        return this.d.isZero();
    }
    compara(o) {
        return this.d.comparedTo(o.d);
    }
    paraBanco() {
        return fixar(this.d, ESCALA.PRECO);
    }
    /** Exibe com 2 casas — o operador não quer ver 6. */
    paraExibicao() {
        return formatarPtBr(this.d, EXIBICAO.DINHEIRO);
    }
    interno() {
        return this.d;
    }
    toString() {
        return this.paraBanco();
    }
    toJSON() {
        return this.paraBanco();
    }
}
/**
 * Rendimento de desdobro: m³ de serrada por m³ de tora (spec funcional §2.6).
 * Expresso como fração 0..1; `paraExibicao` devolve percentual.
 */
export class Rendimento {
    d;
    constructor(d) {
        this.d = d;
    }
    static calcular(entradaTora, saidaSerrada) {
        if (entradaTora.ehZero()) {
            throw new ErroDominio("rendimento: volume de tora é zero");
        }
        if (entradaTora.ehNegativo()) {
            throw new ErroDominio("rendimento: volume de tora é negativo");
        }
        return new Rendimento(saidaSerrada.interno().dividedBy(entradaTora.interno()));
    }
    /** Volume que se perdeu no desdobro. */
    static perda(entradaTora, saidaSerrada) {
        return entradaTora.menos(saidaSerrada);
    }
    fracao() {
        return this.d;
    }
    /** "58,4" — sem o símbolo de percentual. */
    paraExibicao() {
        return formatarPtBr(this.d.times(100), EXIBICAO.PERCENTUAL);
    }
    toJSON() {
        return fixar(this.d, 6);
    }
}
//# sourceMappingURL=grandezas.js.map